import { useMemo } from 'react';

import useEditable from '../../../../../hooks/useEditable';
import usePolicies, { UNSET_POLICY_OPTION } from '../../../../shared/policies/hooks/usePolicies';
import type { SelectOption } from '../../../../../types';
import type { CategoryPolicyOptionsMap } from '../../../../shared/policies/hooks/usePolicies';

export type PoliciesSelectionState = {
  //  Note: we are using `null` to represent the unset value here, for ease with SelectOption.
  //  The backend can handle null or undefined for these fields.
  licensing: SelectOption<number | null>;
  vulnerability: SelectOption<number | null>;
  quality: SelectOption<number | null>;
};

export type UsePoliciesSelectionProps = {
  policies: CategoryPolicyOptionsMap;
  isLoading: boolean;
  errors: string[] | null;
  hasSelectedAtLeastOnePolicy: boolean;
  selectedPolicies: PoliciesSelectionState;
  onUpdateSelectedPolicies: (selectedPolicies: Partial<PoliciesSelectionState>) => void;
  onSaveSelectedPolicies: () => void;
  shouldDisplayLicensingPolicies: boolean;
  shouldDisplaySecurityPolicies: boolean;
  shouldDisplayQualityPolicies: boolean;
};

/**
 * Hook for managing the state of the policies selection page.
 * This hook will fetch all the policies on mount, and then set
 * up an editable state for the user to select those policies.
 */
function usePoliciesSelection({
  licensingId,
  vulnerabilityId,
  qualityId,
}: {
  licensingId?: number | null,
  vulnerabilityId?: number | null,
  qualityId?: number | null,
} = {}): UsePoliciesSelectionProps {
  const {
    policies,
    isLoading,
    errors,
    shouldDisplayLicensingPolicies,
    shouldDisplaySecurityPolicies,
    shouldDisplayQualityPolicies,
  } = usePolicies();

  const initialPoliciesSelection = useMemo((): PoliciesSelectionState => ({
    licensing: policies.licensing.find((policy) => policy.value === licensingId) || UNSET_POLICY_OPTION,
    vulnerability: policies.vulnerability.find((policy) => policy.value === vulnerabilityId) || UNSET_POLICY_OPTION,
    quality: policies.quality.find((policy) => policy.value === qualityId) || UNSET_POLICY_OPTION,
  }), [
    policies,
    licensingId,
    vulnerabilityId,
    qualityId,
  ]);

  const {
    isDirty: hasSelectedAtLeastOnePolicy,
    data: selectedPolicies,
    onUpdate: onUpdateSelectedPolicies,
    onSave: onSaveSelectedPolicies,
  } = useEditable<PoliciesSelectionState>(initialPoliciesSelection);

  return {
    policies,
    isLoading,
    errors,
    hasSelectedAtLeastOnePolicy,
    selectedPolicies,
    onUpdateSelectedPolicies,
    //  Call this when we save the rest of the Create Release Group
    onSaveSelectedPolicies,
    shouldDisplayLicensingPolicies,
    shouldDisplaySecurityPolicies,
    shouldDisplayQualityPolicies,
  };
}

export default usePoliciesSelection;
